import { Option } from "space-monad";
import { useState, useEffect } from "react";
import styled from "styled-components";
import { BillableApi } from "../store/billing/models/internal";
import { Billing } from "../store/billing/reducer";
import { useFetchable } from "../store/common/fetchable";
import { Users } from "../store/users/reducer";
import { colours } from "../styles/colours";
import { getOption } from "../utils/utils";

export function useHasCredit(spec: typeof BillableApi.T): boolean {
  const pricing = useFetchable(Billing.pricing);
  const balance = useFetchable(Billing.balance);
  const currentUser = useFetchable(Users.current);
  const vaultsBilling = useFetchable(Billing.vaultsBilling);
  const chainBilling = useFetchable(Billing.chainBilling);
  const verificationBilling = useFetchable(Billing.verificationBilling);

  const [hasCredit, setHasCredit] = useState<boolean>(false);

  useEffect(() => {
    Option.all([
      getOption(pricing),
      getOption(balance),
      getOption(currentUser),
      getOption(vaultsBilling),
      getOption(chainBilling),
      getOption(verificationBilling),
    ]).fold(
      () => {
        setHasCredit(false);
      },
      ([pric, bal, curr, vaults, chain, verification]) => {
        const freeCredsMap: Partial<Record<typeof BillableApi.T, number>> = {};

        [vaults, chain, verification].forEach((service) =>
          service.apiSpecificInfo.forEach(
            (x) => (freeCredsMap[x.apiName] = x.remainingApiCalls)
          )
        );

        if (freeCredsMap[spec] || chain.remainingPlatformCalls) {
          setHasCredit(true);
          return;
        }

        const originalPrice = pric.featureDetails.find(
          (x) => x.featureName === spec
        )?.originalPrice;

        if (originalPrice === undefined) {
          setHasCredit(false);
          return;
        }

        const levelDiscount =
          pric.levelDiscounts.find((x) => x.userLevel === curr.level)
            ?.discountRate || 0;

        const believerDiscount =
          (curr.believer &&
            pric.levelDiscounts.find((x) => x.userLevel === "BELIEVER")
              ?.discountRate) ||
          0;

        setHasCredit(
          bal.credits >=
            originalPrice * (1 - (levelDiscount + believerDiscount))
        );
      }
    );
  }, [
    pricing,
    balance,
    currentUser,
    vaultsBilling,
    chainBilling,
    verificationBilling,
  ]);

  return hasCredit;
}

export const ErrorText = styled.p`
  color: ${colours.red};
  text-align: center;
`;
